import React, { useState, useEffect, useCallback } from 'react';
import { DollarSign, Users, MessageCircle, AlertTriangle, Loader2, RefreshCw, Zap, Key } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './AICostDashboard.css';

function AICostDashboard() {
  const [tab, setTab] = useState('costs');
  const [costs, setCosts] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [chatUsers, setChatUsers] = useState(null);
  const [recent, setRecent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [costData, alertData, userData] = await Promise.all([
        apiClient.get('/api/ai-costs/summary').catch(() => null),
        apiClient.get('/api/ai-costs/alerts').catch(() => null),
        apiClient.get('/api/chat-access').catch(() => null),
      ]);
      setCosts(costData);
      setAlerts(alertData);
      setChatUsers(userData);
    } catch (_) {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleChatAccess = async (userId, enabled) => {
    setSaving(userId);
    try {
      await apiClient.put(`/api/chat-access/${userId}`, { enabled });
      setChatUsers(prev => ({
        ...prev,
        users: prev.users.map(u => u.user_id === userId ? { ...u, enabled } : u)
      }));
    } catch (err) {
      alert('Failed to update: ' + err.message);
    }
    setSaving(null);
  };

  const loadRecent = async () => {
    try {
      const data = await apiClient.get('/api/ai-costs/recent?limit=30');
      setRecent(data);
    } catch (_) {}
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
      <Loader2 size={32} className="ai-spin" style={{ color: '#94a3b8' }} />
    </div>;
  }

  const t = costs?.totals || {};
  const dailyCost = alerts?.daily_cost || 0;
  const monthlyCost = alerts?.monthly_cost || 0;
  const activeAlerts = alerts?.alerts || [];

  return (
    <div className="ai-cost-container">
      <div className="ai-cost-header">
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Zap size={22} style={{ color: '#f59e0b' }} /> AI Usage & Costs
          </h1>
          <div className="ai-cost-subtitle">Track spending across all AI features — chat, classification, analysis</div>
        </div>
        <button onClick={fetchData} style={{ background: 'var(--color-surface-hover)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 8, color: 'var(--color-text-muted)', cursor: 'pointer' }}>
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Alerts */}
      {activeAlerts.map((a, i) => (
        <div key={i} className={`ai-alert ai-alert-${a.level}`}>
          <AlertTriangle size={16} /> {a.message}
        </div>
      ))}

      {/* Tabs */}
      <div className="ai-tabs">
        <button className={`ai-tab ${tab === 'costs' ? 'ai-tab-active' : ''}`} onClick={() => setTab('costs')}>
          <DollarSign size={14} style={{ marginRight: 4, verticalAlign: -2 }} /> Costs
        </button>
        <button className={`ai-tab ${tab === 'access' ? 'ai-tab-active' : ''}`} onClick={() => setTab('access')}>
          <Users size={14} style={{ marginRight: 4, verticalAlign: -2 }} /> Chat Access
        </button>
        <button className={`ai-tab ${tab === 'recent' ? 'ai-tab-active' : ''}`} onClick={() => { setTab('recent'); if (!recent) loadRecent(); }}>
          <MessageCircle size={14} style={{ marginRight: 4, verticalAlign: -2 }} /> Recent Calls
        </button>
      </div>

      {/* Costs Tab */}
      {tab === 'costs' && (
        <>
          <div className="ai-cost-stats">
            <div className="ai-cost-stat">
              <div className="ai-cost-stat-label">Total Cost</div>
              <div className="ai-cost-stat-value">${parseFloat(t.total_cost_usd || 0).toFixed(2)}</div>
              <div className="ai-cost-stat-sub">{(t.total_calls || 0).toLocaleString()} calls</div>
            </div>
            <div className="ai-cost-stat">
              <div className="ai-cost-stat-label">Today</div>
              <div className="ai-cost-stat-value" style={{ color: dailyCost > 5 ? '#f59e0b' : undefined }}>${dailyCost.toFixed(4)}</div>
              <div className="ai-cost-stat-sub">last 24h</div>
            </div>
            <div className="ai-cost-stat">
              <div className="ai-cost-stat-label">This Month</div>
              <div className="ai-cost-stat-value" style={{ color: monthlyCost > 50 ? '#f59e0b' : undefined }}>${monthlyCost.toFixed(2)}</div>
              <div className="ai-cost-stat-sub">since 1st</div>
            </div>
            <div className="ai-cost-stat">
              <div className="ai-cost-stat-label">Total Tokens</div>
              <div className="ai-cost-stat-value">{((t.total_tokens || 0) / 1000).toFixed(0)}k</div>
              <div className="ai-cost-stat-sub">{((t.total_input_tokens || 0) / 1000).toFixed(0)}k in / {((t.total_output_tokens || 0) / 1000).toFixed(0)}k out</div>
            </div>
          </div>

          {/* By Source */}
          {costs?.by_source?.length > 0 && (
            <div className="ai-cost-section">
              <div className="ai-cost-section-header">Cost by Feature</div>
              <table className="ai-cost-table">
                <thead><tr><th>Source</th><th>Calls</th><th>Tokens</th><th>Cost</th></tr></thead>
                <tbody>
                  {costs.by_source.map((r, i) => (
                    <tr key={i}><td>{r.source}</td><td>{r.calls}</td><td>{(r.tokens / 1000).toFixed(0)}k</td><td>${parseFloat(r.cost_usd).toFixed(4)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* By Model */}
          {costs?.by_model?.length > 0 && (
            <div className="ai-cost-section">
              <div className="ai-cost-section-header">Cost by Model</div>
              <table className="ai-cost-table">
                <thead><tr><th>Model</th><th>Provider</th><th>Calls</th><th>Tokens</th><th>Cost</th></tr></thead>
                <tbody>
                  {costs.by_model.map((r, i) => (
                    <tr key={i}><td>{r.model}</td><td>{r.provider}</td><td>{r.calls}</td><td>{(r.tokens / 1000).toFixed(0)}k</td><td>${parseFloat(r.cost_usd).toFixed(4)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* By User */}
          {costs?.by_user?.length > 0 && (
            <div className="ai-cost-section">
              <div className="ai-cost-section-header">Cost by User</div>
              <table className="ai-cost-table">
                <thead><tr><th>User</th><th>Calls</th><th>Tokens</th><th>Cost</th></tr></thead>
                <tbody>
                  {costs.by_user.map((r, i) => (
                    <tr key={i}><td>{r.user_email || 'system'}</td><td>{r.calls}</td><td>{(r.tokens / 1000).toFixed(0)}k</td><td>${parseFloat(r.cost_usd).toFixed(4)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Chat Access Tab */}
      {tab === 'access' && chatUsers && (
        <div className="ai-cost-section">
          <div className="ai-cost-section-header">Chat Access per User</div>
          {(chatUsers.users || []).map(u => (
            <div className="ai-access-row" key={u.user_id}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                <span className="ai-access-email">{u.email}</span>
                <span className="ai-access-role">{u.role}</span>
                {u.own_api_key_prefix && <span className="ai-access-key"><Key size={10} /> {u.own_api_key_prefix}</span>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {u.tokens_used_this_month > 0 && (
                  <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                    {(u.tokens_used_this_month / 1000).toFixed(0)}k / {((u.monthly_token_limit || 500000) / 1000).toFixed(0)}k tokens
                  </span>
                )}
                <label style={{ position: 'relative', width: 44, height: 24, flexShrink: 0 }}>
                  <input
                    type="checkbox"
                    checked={u.enabled || false}
                    onChange={e => toggleChatAccess(u.user_id, e.target.checked)}
                    disabled={saving === u.user_id}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span style={{
                    position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                    background: u.enabled ? '#3b82f6' : 'var(--color-border, rgba(255,255,255,0.15))',
                    borderRadius: 24, transition: 'all 0.25s',
                  }}>
                    <span style={{
                      position: 'absolute', height: 18, width: 18, left: u.enabled ? 23 : 3, bottom: 3,
                      background: 'white', borderRadius: '50%', transition: 'all 0.25s',
                    }} />
                  </span>
                </label>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recent Calls Tab */}
      {tab === 'recent' && (
        <div className="ai-cost-section">
          <div className="ai-cost-section-header">Recent AI Calls</div>
          {recent ? (
            <table className="ai-cost-table">
              <thead><tr><th>Time</th><th>User</th><th>Source</th><th>Model</th><th>Tokens</th><th>Cost</th></tr></thead>
              <tbody>
                {(recent.calls || []).map((r, i) => (
                  <tr key={i}>
                    <td style={{ fontSize: '0.7rem' }}>{new Date(r.created_at).toLocaleString()}</td>
                    <td>{r.user_email || '-'}</td>
                    <td>{r.source}</td>
                    <td style={{ fontSize: '0.7rem' }}>{r.model?.split('-').slice(-2).join('-')}</td>
                    <td>{r.total_tokens?.toLocaleString()}</td>
                    <td>${parseFloat(r.estimated_cost_usd || 0).toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>
              <Loader2 size={24} className="ai-spin" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AICostDashboard;
